<template>
  <BaseModal modal-content-class="rest confirm_purch">
    <div class="content">
      <h1>{{ $_translations.value.modal_purchase_via_pin_title }}</h1>

      <BaseImage
        src="/static/images/store/store_confirm_sms.svg"
        alt="pin icon"
        class="top_img"
      />

      <p class="games-to-price"><span>{{ quantityText }} - {{ priceText }}</span></p>

      <p>{{ $_translations.value.modal_purchase_via_pin_description }}</p>

      <BaseDigitFormInput
        :model-value="formValues.pin.state"
        :label="$_translations.value.modal_purchase_via_pin_input_label_validation_code"
        :error="formValues.pin.validation.error"
        :error-message="$_translations.value[formValues.pin.validation.message]"
        :max-length="appSettingsStore.appProperties.maxLengthPinDigits"
        input-id="pin_sms"
        @update:modelValue="formValues.pin.setState"
      />

      <BaseButton
        :disabled="submitting"
        @click:button="handlePurchase"
      >
        {{ $_translations.value.modal_purchase_via_pin_button_proceed }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseDigitFormInput from '@/components/formInput/BaseDigitFormInput.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import { apiPurchaseBundle } from '@/services/bundle.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const emits = defineEmits(['purchase:viaPin']);

const props = defineProps({
  bundleId: {
    type: [String, Number],
    default: null,
  },
  bundleType: {
    type: String,
    default: '',
  },
  quantityText: {
    type: String,
    default: '',
  },
  priceText: {
    type: String,
    default: '',
  },
});

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();

const { formValues, isFormInvalid, resetFormErrors } = useFormHook({ pin: '' });
const [submitting, setSubmittingState] = useUILockHook();

const validateForm = () => {
  if (!formValues.pin.state) {
    formValues.pin.setValidation({ error: true, message: 'common_error_input_field_required' });
  }

  return isFormInvalid();
};

const handlePurchase = async () => {
  setSubmittingState(true);

  resetFormErrors();

  const invalid = validateForm();

  if (invalid) {
    setSubmittingState(false);
    return;
  }

  const { responseEnum } = await apiPurchaseBundle({
    payload: {
      bundleId: props.bundleId,
      pin: formValues.pin.state,
    },
  });

  if (responseEnum === apiResponseTypeEnum.WRONG_PIN) {
    formValues.pin.setValidation({ error: true, message: 'modal_purchase_via_pin_error_validation_code_invalid' });
  } else if (responseEnum === apiResponseTypeEnum.OPERATION_SUCCESS) {
    await authStore.dispatchRetrieveAuthUser().catch((error) => error);

    emits('purchase:viaPin', { status: 'success' });
  } else if (responseEnum === apiResponseTypeEnum.NO_BALANCE) {
    emits('purchase:viaPin', { status: 'error' });
  } else {
    emits('purchase:viaPin', { status: 'error_pin' });
  }

  setSubmittingState(false);
};
</script>
